import { useTranslations } from '@vocab/react';
import {
  Stack,
  RadioGroup,
  RadioItem,
  Dropdown,
  Box,
  IconSubCategory,
  Columns,
  Column,
} from 'braid-design-system';
import { type FormEvent, useEffect, useState } from 'react';

import lysTranslations from 'src/pages/LinkYourSoftware/.vocab';

interface Props {
  id: string;
  radioLabel: string;
  radioOptions?: [string, string];
  dropdownLabel: string;
  dropdownOptions: string[];
  error?: boolean;
  value: Record<string, any> | RadioWithFollowUpState | undefined;
  onChange: (value: RadioWithFollowUpState | undefined) => void;
}

export interface RadioWithFollowUpState {
  answer: string;
  followUpAnswer?: string;
}

/*
  Component designed to be consumed within a
  FormProvider (forms-ui) which is controlling the state
*/
export const RadioWithFollowUp = ({
  id,
  radioLabel,
  radioOptions = ['No', 'Yes'],
  dropdownLabel,
  dropdownOptions,
  error,
  value,
  onChange,
}: Props) => {
  const [state, setState] = useState<RadioWithFollowUpState>();

  const dropdownChange = (event: FormEvent<HTMLSelectElement>) => {
    const followUpResponse = (event.target as HTMLSelectElement).value;
    const newValue: RadioWithFollowUpState = {
      answer: radioOptions[1],
      followUpAnswer: followUpResponse,
    };
    setState(newValue);
  };

  const radioChange = (event: FormEvent<HTMLInputElement>) => {
    const answer = event.currentTarget.value;
    const newAnswer = { answer };
    setState(newAnswer);
  };

  const { t: lys } = useTranslations(lysTranslations);

  useEffect(() => {
    onChange(state);
  }, [state, onChange]);

  return (
    <Stack space="small">
      <RadioGroup
        required
        id={`${id}-radio-group`}
        value={value?.answer}
        onChange={radioChange}
        label={radioLabel}
        tone={error && !state?.answer ? 'critical' : 'neutral'}
        message={error && !state?.answer && lys('selectOneOption')}
      >
        {radioOptions.map((option) => (
          <RadioItem key={option} label={option} value={option} />
        ))}
      </RadioGroup>
      {value?.answer === radioOptions[1] && (
        <Columns space="xsmall">
          <Column width="content">
            <Box paddingLeft="xxsmall">
              <IconSubCategory size="large" />
            </Box>
          </Column>
          <Column>
            <Box paddingTop="small">
              <Dropdown
                id={`${id}-radio-input`}
                label={dropdownLabel}
                required
                onChange={dropdownChange}
                value={value.followUpAnswer}
                placeholder={lys('pleaseSelect')}
                tone={error ? 'critical' : 'neutral'}
                message={error && lys('selectOneOption')}
              >
                {dropdownOptions.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </Dropdown>
            </Box>
          </Column>
        </Columns>
      )}
    </Stack>
  );
};
